import type { BoardDetail, Project } from "./types";

/** A card naming a project other than its board's default; `foreign` when the board does not cover it. */
export interface ProjectOverride {
  project: Project;
  foreign: boolean;
}

/**
 * The ids of every project a board covers, in its order. The default leads when it
 * is missing from the set, as core reads it, so a board from before sets existed
 * (or a cached detail without `projects`) still names its one project.
 */
export function boardProjectIds(detail: Pick<BoardDetail, "board" | "projects"> | null | undefined): string[] {
  if (!detail) return [];
  const ids = (detail.projects ?? []).map((project) => project.id);
  const fallback = detail.board.projectId;
  return fallback && !ids.includes(fallback) ? [fallback, ...ids] : ids;
}

/** Whether the board covers this project, as its default or one of several. */
export function boardCovers(detail: Pick<BoardDetail, "board" | "projects"> | null | undefined, projectId: string | null): boolean {
  return projectId !== null && boardProjectIds(detail).includes(projectId);
}

/**
 * Projects split for a card's picker: the board's own first, in its order, then
 * every other registered project by name, as the picker always listed them.
 */
export function splitProjectsForBoard(projects: Project[], boardIds: string[]): { onBoard: Project[]; others: Project[] } {
  const byId = new Map(projects.map((project) => [project.id, project]));
  const onBoard = boardIds.flatMap((id) => byId.get(id) ?? []);
  return { onBoard, others: projects.filter((project) => !boardIds.includes(project.id)) };
}
